import { useEffect, useLayoutEffect, useRef } from 'react'
import { BASEMAP_IDS } from '../lib/basemaps.js'
import { IconClose } from './Icons.jsx'

const BASEMAP_LABEL = { minimal: 'basemapMinimal', detailed: 'basemapDetailed', streets: 'basemapStreets' }

/** A tiny illustration of each basemap's look (no network, works offline). */
function MapThumb({ id, theme }) {
  const dark = theme === 'dark'
  const P =
    id === 'minimal'
      ? dark
        ? { bg: '#151515', park: '#1d201d', water: '#232326', road: '#3a3a3a', main: '#4a4a4a', bld: '#1c1c1c' }
        : { bg: '#f2f3f0', park: '#e3e8e1', water: '#c2c8ca', road: '#ffffff', main: '#ffffff', bld: '#e8e8e4' }
      : id === 'detailed'
        ? dark
          ? { bg: '#45516e', park: '#3d5267', water: '#38435c', road: '#6b7a99', main: '#a5b1c9', bld: '#4e5b78' }
          : { bg: '#f8f4f0', park: '#d8e8c8', water: '#9ebdff', road: '#ffffff', main: '#fbcf8f', bld: '#e6ddd5' }
        : { bg: '#f2efe9', park: '#add19e', water: '#aad3df', road: '#ffffff', main: '#f9b29c', bld: '#d9d0c9' }
  return (
    <svg className={`mapthumb__img ${id === 'streets' && dark ? 'is-dimmed' : ''}`} viewBox="0 0 72 48" aria-hidden="true">
      <rect width="72" height="48" fill={P.bg} />
      <path d="M0 34c12-6 22-2 34 2s24 6 38-4v16H0z" fill={P.water} />
      <rect x="46" y="6" width="18" height="12" rx="3" fill={P.park} />
      <rect x="8" y="8" width="10" height="8" rx="1.5" fill={P.bld} />
      <rect x="22" y="12" width="8" height="10" rx="1.5" fill={P.bld} />
      <path d="M-2 22 74 14" stroke={P.road} strokeWidth="3" />
      <path d="M28 -2 36 50" stroke={P.main} strokeWidth="4" />
      {id !== 'minimal' && <path d="M-2 4 40 28" stroke={P.road} strokeWidth="2" />}
    </svg>
  )
}

/** Whose map this is — the licences want the credit, the map doesn't need it on top. */
function MapCredits({ t, basemap }) {
  const osm = (
    <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">
      © OpenStreetMap
    </a>
  )
  return (
    <p className="foot__source">
      {t('mapSource')}:{' '}
      {basemap === 'streets' ? (
        osm
      ) : (
        <>
          <a href="https://openfreemap.org" target="_blank" rel="noopener noreferrer">
            OpenFreeMap
          </a>{' '}
          ·{' '}
          <a href="https://www.openmaptiles.org/" target="_blank" rel="noopener noreferrer">
            © OpenMapTiles
          </a>{' '}
          · {osm}
        </>
      )}
    </p>
  )
}

/**
 * Map settings: base map, what the colours encode, light/dark and language, then
 * the small print (where the data and the map come from). Non-modal — the map
 * stays live behind it; Esc, the ×, or a tap outside closes it.
 */
export default function LayersPopover({
  t,
  theme,
  basemap,
  setBasemap,
  colorMode,
  setColorMode,
  durationMin,
  themePref,
  setThemePref,
  lang,
  setLang,
  sourceLine,
  creditsBasemap,
  onClose,
}) {
  const ref = useRef(null)

  useEffect(() => {
    ref.current?.querySelector('[aria-pressed="true"]')?.focus({ preventScroll: true })
    const onDown = (e) => {
      if (!ref.current?.contains(e.target) && !e.target.closest?.('[data-layers-toggle]')) onClose()
    }
    document.addEventListener('pointerdown', onDown, true)
    return () => document.removeEventListener('pointerdown', onDown, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Closing with focus inside (Esc, the ×) hands it back to the toggle instead of
  // dropping it on <body>. A layout cleanup runs while the node is still in the DOM.
  useLayoutEffect(() => {
    const node = ref.current
    return () => {
      if (node.contains(document.activeElement)) document.querySelector('[data-layers-toggle]')?.focus({ preventScroll: true })
    }
  }, [])

  const modes = [
    { id: 'status', label: t('byStatus') },
    { id: 'price', label: t('byPrice'), disabled: !durationMin },
    { id: 'category', label: t('byCategory') },
  ]
  const themes = [
    { id: 'auto', label: t('themeAuto') },
    { id: 'light', label: t('themeLight') },
    { id: 'dark', label: t('themeDark') },
  ]

  return (
    <div ref={ref} className="popover layers" role="dialog" aria-label={t('layersTitle')}>
      <div className="popover__head">
        <h2 className="popover__title">{t('layersTitle')}</h2>
        <button type="button" className="icon-btn icon-btn--sm" aria-label={t('close')} onClick={onClose}>
          <IconClose />
        </button>
      </div>

      <section className="section">
        <h3 className="section__title">{t('mapTypeLabel')}</h3>
        <div className="mapthumbs" role="group" aria-label={t('mapTypeLabel')}>
          {BASEMAP_IDS.map((id) => (
            <button
              key={id}
              type="button"
              className={`mapthumb ${basemap === id ? 'is-active' : ''}`}
              aria-pressed={basemap === id}
              onClick={() => setBasemap(id)}
            >
              <MapThumb id={id} theme={theme} />
              <span className="mapthumb__label">{t(BASEMAP_LABEL[id])}</span>
            </button>
          ))}
        </div>
      </section>

      <section className="section">
        <h3 className="section__title">{t('colorBy')}</h3>
        <div className="seg" role="group" aria-label={t('colorBy')}>
          {modes.map((m) => (
            <button
              key={m.id}
              type="button"
              className={colorMode === m.id ? 'is-active' : ''}
              aria-pressed={colorMode === m.id}
              disabled={m.disabled}
              onClick={() => setColorMode(m.id)}
            >
              {m.label}
            </button>
          ))}
        </div>
        {!durationMin && <p className="hint">{t('priceNeedsStay')}</p>}
      </section>

      <section className="section">
        <h3 className="section__title">{t('appearanceLabel')}</h3>
        <div className="seg" role="group" aria-label={t('appearanceLabel')}>
          {themes.map((m) => (
            <button
              key={m.id}
              type="button"
              className={themePref === m.id ? 'is-active' : ''}
              aria-pressed={themePref === m.id}
              onClick={() => setThemePref(m.id)}
            >
              {m.label}
            </button>
          ))}
        </div>
      </section>

      <section className="section">
        <h3 className="section__title">{t('languageLabel')}</h3>
        <div className="seg" role="group" aria-label={t('languageLabel')}>
          {['cs', 'en'].map((l) => (
            <button key={l} type="button" className={lang === l ? 'is-active' : ''} aria-pressed={lang === l} onClick={() => setLang(l)} lang={l}>
              {l === 'cs' ? 'Čeština' : 'English'}
            </button>
          ))}
        </div>
      </section>

      <footer className="foot">
        <p className="disclaimer">{t('disclaimer')}</p>
        <p className="foot__source" title={t('snapshotNote')}>
          {sourceLine}
        </p>
        <MapCredits t={t} basemap={creditsBasemap} />
      </footer>
    </div>
  )
}
